import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import Mascot from '@/components/mascot/mascot';

type MascotPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface MascotContextType {
  showMascot: (options?: {
    message?: string;
    position?: MascotPosition;
    mascotType?: string;
    autoHide?: boolean;
    autoHideDelay?: number;
  }) => boolean;
  hideMascot: () => void;
  isMascotVisible: boolean;
  setWelcomeShown: (shown: boolean) => void;
  hasSeenWelcome: boolean;
  shouldShowMascot: () => boolean;
  haasEnabled: boolean;
  setHaasEnabled: (v: boolean) => void;
  reduceAnimations: boolean;
  setReduceAnimations: (v: boolean) => void;
}

const MascotContext = createContext<MascotContextType | undefined>(undefined);

export function MascotProvider({ children }: { children: ReactNode }) {
  const [mascotVisible, setMascotVisible] = useState(false);
  const [welcomeShown, setWelcomeShownState] = useState(false);
  const [haasEnabled, setHaasEnabledState] = useState(true);
  const [reduceAnimations, setReduceAnimationsState] = useState(false);
  const [mascotConfig, setMascotConfig] = useState({
    message: '',
    position: 'bottom-right' as MascotPosition,
    mascotType: 'main',
    autoHide: false,
    autoHideDelay: 5000,
  });

  useEffect(() => {
    const hasShown = localStorage.getItem('mascot-welcome-shown');
    if (hasShown) setWelcomeShownState(true);

    const enabled = localStorage.getItem('haas_enabled');
    if (enabled === 'false') setHaasEnabledState(false);

    const reduce = localStorage.getItem('haas_reduce_animations');
    if (reduce === 'true') setReduceAnimationsState(true);
  }, []);

  const setWelcomeShown = useCallback((shown: boolean) => {
    setWelcomeShownState(shown);
    if (shown) localStorage.setItem('mascot-welcome-shown', 'true');
  }, []);

  const setHaasEnabled = useCallback((v: boolean) => {
    setHaasEnabledState(v);
    localStorage.setItem('haas_enabled', String(v));
    if (!v) setMascotVisible(false);
  }, []);

  const setReduceAnimations = useCallback((v: boolean) => {
    setReduceAnimationsState(v);
    localStorage.setItem('haas_reduce_animations', String(v));
  }, []);

  const showMascot = useCallback((options?: {
    message?: string;
    position?: MascotPosition;
    mascotType?: string;
    autoHide?: boolean;
    autoHideDelay?: number;
  }): boolean => {
    if (!haasEnabled) return false;
    if (!options || !options.message) return false;

    try {
      setMascotConfig({
        message: options.message,
        position: options.position || 'bottom-right',
        mascotType: options.mascotType || 'main',
        autoHide: options.autoHide !== undefined ? options.autoHide : false,
        autoHideDelay: options.autoHideDelay || 5000,
      });
      setMascotVisible(true);
      return true;
    } catch {
      return false;
    }
  }, [haasEnabled]);

  const hideMascot = useCallback(() => {
    setMascotVisible(false);
  }, []);

  const shouldShowMascot = useCallback((): boolean => {
    return haasEnabled && !welcomeShown;
  }, [haasEnabled, welcomeShown]);

  return (
    <MascotContext.Provider
      value={{
        showMascot,
        hideMascot,
        isMascotVisible: mascotVisible,
        setWelcomeShown,
        hasSeenWelcome: welcomeShown,
        shouldShowMascot,
        haasEnabled,
        setHaasEnabled,
        reduceAnimations,
        setReduceAnimations,
      }}
    >
      {children}
      {mascotVisible && haasEnabled && (
        <Mascot
          message={mascotConfig.message}
          position={mascotConfig.position}
          onClose={hideMascot}
          mascotType={mascotConfig.mascotType}
          autoHide={mascotConfig.autoHide}
          autoHideDelay={mascotConfig.autoHideDelay}
          reduceAnimations={reduceAnimations}
        />
      )}
    </MascotContext.Provider>
  );
}

export function useMascot() {
  const context = useContext(MascotContext);
  if (context === undefined) {
    throw new Error('useMascot must be used within a MascotProvider');
  }
  return context;
}
